import { MbscCalendarEvent } from '@mobiscroll/react';
import moment from 'moment';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';

import { fetchAllEvents, fetchAllEventTypes } from 'core/API/events';
import { getDays, getOrganizations } from 'core/API/organizationSettings';
import {
	PutOneShift,
	fetchAllShifts,
	DeleteShift,
	publishShiftsInRange,
	PostShifts,
	Shift,
} from 'core/API/shifts';
import {
	getGeneralSettings,
	getJobCodes,
	updateSchedulerMetric,
	getSchedulerMetrics,
} from 'core/API/scheduler';
import {
	getShiftRequests,
	deleteShiftRequest,
	getShiftRequestTypes,
	approveOrDenyShiftRequest,
} from 'core/API/shift-requests';
import { ShiftRequest } from 'types/shift-requests';
import { cacheShiftRequestTypes } from 'core/utils/shiftRequestTypesCache';
import { IUsers } from 'core/interfaces/userInterface';
import { paginationService } from 'core/services/paginationService';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useIsMount } from 'core/hooks/useIsMounted';
import { useMobileDetect } from 'core/hooks/useMobileDetect';
import useModal from 'core/hooks/useModal';
import useSearchQuery from 'core/hooks/useQuery';
import { useAuth } from 'module/auth/context/AuthContext';
import { useDocumentContext } from 'module/documents/context';
import AddShiftPage, { IExtendedCellClick } from './AddShiftPage/AddShiftPage';
import EditStatsPage from './EditStatsPage/EditStatsPage';
import CopySchedulePage from './CopySchedulePage/CopySchedulePage';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import getTimeWithTimezone from 'shared/utils/dates/formatTimeWithTimezone';

import { CalendarType, TimeView } from '../interfaces/ScheduleInterface';
import { IShiftPayload } from '../interfaces/ShiftsInterface';
import DesktopPage from './DesktopPage/DesktopPageSchedule';
import MobilePage from './MobilePage/MobilePage';
import PublishShiftsModal from './PublishShiftsModal';
import ConfirmDialog from '../Modals/ConfirmDialog/ConfirmDialog';
import EmployeeRequestModal from '../components/EmployeeRequestModal/EmployeeRequestModal';
import RequestDetailView from '../components/RequestDetailView/RequestDetailView';
import ScheduleStatsBox from '../components/ScheduleStatsBox/ScheduleStatsBox';
import { usePermissionCheck } from 'core/hooks/usePermissionCheck';

import './SchedulePage.style.scss';
import Grid from '@mui/material/Grid';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button as MuiButton,
	Typography as MuiTypography,
	Box,
	CircularProgress,
} from '@mui/material';
import { useCalendarView } from 'core/hooks/useCalendarView';
import { getScheduleDateRange } from './SchedulePage.utils';

import momentTZ from 'moment-timezone';

momentTZ.tz.add(
	'America/Detroit|LMT CST EST EWT EPT EDT|5w.b 60 50 40 40 40|0123425252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252|-2Cgir.N peqr.N 156L0 8x40 iv0 6fd0 11z0 JxX1 SMX 1cN0 1cL0 aW10 1cL0 s10 1Vz0 1cN0 1cL0 1cN0 1fz0 1a10 1fz0 1cN0 1cL0 1cN0 1cL0 1cN0 1cL0 1cN0 1cL0 1cN0 1fz0 1a10 1fz0 1cN0 1cL0 1cN0 1cL0 1cN0 1cL0 14p0 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0 14p0 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0|37e5'
);

export interface ICellClick {
	start_at: Date;
	end_at?: Date;
	resource: number;
	department?: number;
	roles?: number;
}

// Helper function to check if a date is in the past
const isDateInPast = (date: Date | string): boolean => {
	const inputDate = moment(date).startOf('day');
	const today = moment().startOf('day');
	return inputDate.isBefore(today);
};

export default function SchedulePage(): JSX.Element {
	const _auth = useAuth();
	const _isMounted = useIsMount();

	// Permission checks
	const { hasViewPermission, hasEditPermission, hasDeletePermission, hasExecutePermission } =
		usePermissionCheck();

	// Scheduler permission checks
	const canApplyForRequests = hasExecutePermission('Schedule - My');

	// Scheduler all employees permission checks
	const canViewAllSchedules = hasViewPermission('Schedule - All');
	const canEditShifts = hasEditPermission('Schedule - All');
	const canDeleteShiftsAll = hasDeletePermission('Schedule - All');
	// const canDeleteShiftsMy = hasDeletePermission('Schedule - My');
	const canDeleteShifts = canDeleteShiftsAll;
	const canCreateShifts = hasEditPermission('Schedule - All');

	const canManageAllRequests = hasExecutePermission('Schedule - All');

	const canManageShifts = canEditShifts || canDeleteShifts || canCreateShifts;
	const canSubmitRequests = canApplyForRequests;

	// Check if user can publish (Scheduler all employees Execute permission)
	const canPublishShifts = hasExecutePermission('Schedule - All');

	const queryClient = useQueryClient();

	const history = useHistory();
	const { push } = history;

	const { workplaceId, timezones, workplace, data: workplacesData } = useWorkplacesContext();

	const { showToast } = useToastContext();

	// Get businessWeekStart from document context
	const { businessWeekStart, setScheduleView } = useDocumentContext();

	const isMobile = useMobileDetect().isMobile();

	const [editEvent, setEditEvent] = useState(null);
	const [addNewEvent, setAddNewEvent] = useState<IExtendedCellClick>();
	const [currentView, setCurrentView] = useState<
		| 'scheduler'
		| 'add-shift'
		| 'edit-shift'
		| 'employee-request'
		| 'edit-stats'
		| 'copy-schedule'
		| 'request-detail'
	>('scheduler');

	// Sync currentView with context so HeaderBar can access it
	useEffect(() => {
		setScheduleView(currentView);
	}, [currentView, setScheduleView]);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
	const [shiftToDelete, setShiftToDelete] = useState<any>(null);
	const [openPublishModal, setOpenPublishModal] = useState(false);
	const [selectedEvent, setSelectedEvent] = useState<any>(null);
	const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
	const [editRequest, setEditRequest] = useState<ShiftRequest | null>(null);
	const [deleteRequestConfirmOpen, setDeleteRequestConfirmOpen] = useState(false);
	const [requestToDelete, setRequestToDelete] = useState<number | null>(null);
	const [selectedRequestForDetail, setSelectedRequestForDetail] = useState<ShiftRequest | null>(
		null
	);

	// ************************ USE MODAL HOOKS ************************
	const {
		open: _isAddShiftModalOpen,
		openModal: _openAddShiftModal,
		closeModal: _closeAddShiftModal,
	} = useModal();

	const {
		open: _isEditShiftModalOpen,
		openModal: _openEditShiftModal,
		closeModal: _closeEditShiftModal,
	} = useModal();

	const {
		open: _isAddEventModalOpen,
		openModal: _openAddEventModal,
		closeModal: _closeAddEventModal,
	} = useModal();

	const {
		open: _isEditEventModalOpen,
		openModal: _openEditEventModal,
		closeModal: _closeEditEventModal,
	} = useModal();

	const {
		open: _isRequestAvailableModalOpen,
		openModal: _openRequestAvailableModalOpen,
		closeModal: _closeRequestAvailableModalOpen,
	} = useModal();

	const {
		open: _isTradeRequestModalOpen,
		openModal: _openTradeRequestModal,
		closeModal: _closeTradeRequestModal,
	} = useModal();

	const {
		open: _isTimeOffRequestModalOpen,
		openModal: _openTimeOffRequestModal,
		closeModal: _closeTimeOffRequestModal,
	} = useModal();

	const {
		open: _isShiftPickModalOpen,
		openModal: _openShiftPickModal,
		closeModal: _closeShiftPickModal,
	} = useModal();

	const {
		open: _isCoverRequestModalOpen,
		openModal: _openCoverRequestModal,
		closeModal: _closeCoverRequestModal,
	} = useModal();

	const {
		open: _isCustomerDatabaseModalOpen,
		openModal: _openCustomerDatabaseModal,
		closeModal: _closeCustomerDatabaseModal,
	} = useModal();

	// copy schedule modal
	const {
		open: _isCopyScheduleOpen,
		openModal: _openCopySchedule,
		closeModal: _closeCopySchedule,
	} = useModal();

	const [_copyFromDate, _setCopyFromDate] = useState('');
	const [_copyToDate, _setCopyToDate] = useState('');

	// Fetch organization settings to get start_of_day
	const { data: organizationData } = useQuery('organizations', () => getOrganizations(), {
		staleTime: 300000,
	});

	// Fetch general settings for scheduler (minor age, overtime settings)
	const { data: generalSettings } = useQuery('generalSettings', () => getGeneralSettings(), {
		staleTime: 300000,
	});

	// Fetch all job codes for day view resources
	const { data: jobCodesData } = useQuery('jobCodes', () => getJobCodes(), {
		staleTime: 300000,
	});

	// Fetch days data for availability checking
	const { data: daysData } = useQuery(['days'], () => getDays(), {
		staleTime: 300000,
	});

	// Fetch workplace users for dateOfBirth and other employee details
	// Note: Using GetParticipantsList which calls users-workplaces endpoint
	const { data: allUsersRaw } = useQuery(
		['users-list', workplaceId],
		async () => {
			const data = await paginationService.getWithHandler(`users`, undefined, 100);
			return data?.member || data?.['hydra:member'];
		},
		{
			staleTime: 300000,
			enabled: !!workplaceId,
		}
	);

	// Fetch workplace-specific user IDs
	const { data: workplaceUserIds } = useQuery(
		['workplace-users', workplaceId],
		async () => {
			if (!workplaceId) return [];
			const data = await paginationService.getWithHandler(
				`/workplaces/users?workplace=${workplaceId}`,
				undefined,
				100
			);
			// Extract user IDs or @id references from workplace users
			const users = Object.values(data);
			const userArray = Array.isArray(users) ? users : Object.values(users);
			// Return array of user @id references for filtering
			return userArray.map((user: any) => user['@id'] || `/api/users/${user.id}`);
		},
		{
			staleTime: 300000,
			enabled: !!workplaceId,
		}
	);

	// Transform the raw API response to extract user data with @id and dateOfBirth
	const allUsers = useMemo(() => {
		if (!allUsersRaw || !generalSettings) return [];
		const usersArray = Object.values(allUsersRaw as Array<IUsers>);
		const minorAge = generalSettings?.ageOfMinor || 18;

		return usersArray.map((user: IUsers) => {
			// Calculate isMinor for each user
			let isMinor = false;
			if (user.dateOfBirth) {
				const age = moment().diff(moment(user.dateOfBirth), 'years');
				isMinor = age < minorAge;
			}

			return {
				'@id': user['@id'],
				id: user.id,
				firstName: user.firstName?.trim() || '',
				lastName: user.lastName?.trim() || '',
				email: user.email || '',
				dateOfBirth: user.dateOfBirth || null,
				userAvailability: user.userAvailability || [],
				userTimeOff: user.userTimeOff || [],
				isMinor: isMinor,
			};
		});
	}, [allUsersRaw, generalSettings]);
	// Filter users to only those in the current workplace
	const workplaceUsers = useMemo(() => {
		if (!allUsers || !workplaceUserIds) return allUsers;

		return allUsers.filter((user) => {
			const userId = user['@id'] || `/api/users/${user.id}`;
			return workplaceUserIds.includes(userId);
		});
	}, [allUsers, workplaceUserIds]);
	// Get start_of_day from organization (default to '00:00' if not found)
	const startOfDay = useMemo(() => {
		return organizationData?.data?.[0]?.start_of_day || '00:00';
	}, [organizationData]);

	// Get start_of_week from organization (default to 'Monday' if not found)
	// Convert from /api/days/id format to day name using daysData
	const startOfWeek = useMemo(() => {
		const rawStartOfWeek = organizationData?.data?.[0]?.start_of_week;

		// If rawStartOfWeek is in /api/days/id format, convert it
		if (rawStartOfWeek && rawStartOfWeek.startsWith('/api/days/') && daysData) {
			const dayEntry = daysData.find((d: any) => d['@id'] === rawStartOfWeek);
			return dayEntry?.label || 'Monday';
		}

		// Otherwise return as-is or default to Monday
		return rawStartOfWeek || 'Monday';
	}, [organizationData, daysData]);

	// ************************************************************************

	// url query string determin what viwe
	const location = useSearchQuery();

	const isValidParam = (paramName: string) => {
		const paramValue = location.get(paramName);
		let isValid = true;

		if (paramValue === 'undefined') {
			isValid = false;
		}
		if (paramValue === 'null') {
			isValid = false;
		}
		if (!paramValue) {
			isValid = false;
		}
		return isValid ? location.get(paramName) : paramName === 'time' ? 'day' : 'schedule';
	};

	// ************************************************************************

	const [timeView, _setView] = useState<TimeView>(isValidParam('time') as TimeView);
	// for event i have to leave this date state
	const [fromDate, setFromDate] = useState(() => {
		const [formatedFirstDay] = getScheduleDateRange(
			timezones,
			timeView,
			isMobile,
			startOfWeek,
			daysData
		);
		return formatedFirstDay;
	});

	const [toDate, setToDate] = useState(() => {
		const [, formatedLastDay] = getScheduleDateRange(
			timezones,
			timeView,
			isMobile,
			startOfWeek,
			daysData
		);
		return formatedLastDay;
	});

	// Update fromDate and toDate when startOfWeek changes (e.g., when organization data loads)
	useEffect(() => {
		const [formatedFirstDay, formatedLastDay] = getScheduleDateRange(
			timezones,
			timeView,
			isMobile,
			startOfWeek,
			daysData
		);
		setFromDate(formatedFirstDay);
		setToDate(formatedLastDay);
	}, [startOfWeek, timezones, timeView, isMobile, daysData]);

	const [_toPublish, _setToPublish] = useState(0);
	const [_conflicts, _setConflicts] = useState<Array<any>>([]);
	const [_isPublishable, setIsPublishable] = useState<boolean>(false);

	const [allSchedules, setAllSchedules] = useState<MbscCalendarEvent[]>([]);
	const [_schedulesBackup, _setSchedulesBackup] = useState<MbscCalendarEvent[]>([]);
	const [_schedulesUnassigned, _setSchedulesUnassigned] = useState<any>([]);
	const [allEvents, setAllEvents] = useState<MbscCalendarEvent[]>([]);
	// CalendarShift extends Shift with calendar-specific properties
	type CalendarShift = Shift & {
		start: Date;
		end: Date;
		shift_id: number;
		editable: boolean;
		role?: string;
		background_color?: string;
		text_color?: string;
		color?: string;
		resource?: string | number;
		title?: string;
		employeeStatusData?: {
			hasOvertime: boolean;
			isApproachingDailyOvertime: boolean;
			isApproachingWeeklyOvertime: boolean;
			overtimeHours: number;
			todayOvertimeHours: number; // Overtime for the specific day
			todayHours: number; // Total hours for the specific day
			hasDailyOvertime: boolean;
			hasWeeklyOvertime: boolean;
			maxDailyHours: number;
			totalHours: number;
			isMinor: boolean;
			hasTimeOff: boolean;
			hasAvailabilityIssue: boolean;
			availabilityMessage?: string | null; // Availability conflict message
			isShiftCausingDailyOT: boolean; // True if THIS shift causes daily overtime
			isShiftCausingWeeklyOT: boolean; // True if THIS shift causes weekly overtime
			dailyOTWarning: number;
			weeklyOTWarning: number;
		} | null;
		employeeTimeOff?: any[]; // Employee's time-off data
		employeeAvailability?: any[]; // Employee's availability data
	};
	const [allShifts, setAllShifts] = useState<CalendarShift[]>([]);

	// Track the last focused date to maintain focus when switching views
	const [lastFocusedDate, setLastFocusedDate] = useState<Date | null>(null);

	const [_requestData, setRequestData] = useState(null);

	const [calendarType, setCalendarType] = useState<CalendarType>(
		isValidParam('view') as CalendarType
	);

	const _usersData = queryClient.getQueryData<IUsers[]>(['users', workplaceId]);

	// Calculate workplace working hours start time for the business week start day
	const workplaceWorkingHoursStart = useMemo(() => {
		// Use workplace from context which has complete data including workplaceWorkingHours
		if (!workplace || !workplace.workplaceWorkingHours) return startOfDay;

		const workingHours = workplace.workplaceWorkingHours;

		if (!workingHours || workingHours.length === 0) return startOfDay;

		// If we don't have daysData or startOfWeek, fall back to earliest time across all days
		if (!daysData || !startOfWeek) {
			const earliestTime = workingHours.reduce((earliest: string, wh: any) => {
				const openTime = wh.openAt || '00:00:00';
				if (!earliest || openTime < earliest) {
					return openTime;
				}
				return earliest;
			}, '');
			return earliestTime ? earliestTime.substring(0, 5) : startOfDay;
		}

		// Create a map of days by their @id or id for quick lookup
		const daysMap = new Map();
		if (Array.isArray(daysData)) {
			daysData.forEach((day: any) => {
				const dayId = day['@id'] || `/api/days/${day.id}`;
				daysMap.set(dayId, day);
			});
		}

		// Determine the target day: if businessWeekStart is in the current week, use current day, otherwise use startOfWeek
		let targetDay = startOfWeek;
		if (businessWeekStart) {
			const currentWeekStart = moment(businessWeekStart);
			const currentWeekEnd = moment(businessWeekStart).add('7', 'days');
			const businessWeekMoment = moment();
			// console.log(
			// 	'check of current week',
			// 	currentWeekStart.format('YYYY-MM-DD'),
			// 	currentWeekEnd.format('YYYY-MM-DD'),
			// 	businessWeekMoment.format('YYYY-MM-DD'),
			// 	businessWeekMoment.isBetween(currentWeekStart, currentWeekEnd, null, '[]')
			// );
			if (businessWeekMoment.isBetween(currentWeekStart, currentWeekEnd, null, '[]')) {
				// businessWeekStart is in the current week, use current day
				targetDay = moment().format('dddd'); // e.g., "Monday", "Tuesday", etc.
			}
		}

		// Find the working hours entry that matches the target day
		const weekStartWorkingHours = workingHours.find((wh: any) => {
			const dayReference = wh.day;
			const dayData = daysMap.get(dayReference);
			// console.log({ dayReference, dayData, startOfWeek });
			// Match the day's label with targetDay (e.g., "Monday")
			// console.log('LOG dayData.label', dayReference, dayData?.label, targetDay);
			return dayData && dayData?.label === targetDay;
		});
		// console.log('LOG weekStartWorkingHours', weekStartWorkingHours);

		// If we found working hours for the week start day, use its openAt time
		if (weekStartWorkingHours && weekStartWorkingHours.openAt) {
			return weekStartWorkingHours.openAt.substring(0, 5);
		}

		// Fallback to earliest time if we couldn't find the specific day
		const earliestTime = workingHours.reduce((earliest: string, wh: any) => {
			const openTime = wh.openAt || '00:00:00';
			if (!earliest || openTime < earliest) {
				return openTime;
			}
			return earliest;
		}, '');

		// Return time in HH:mm format
		return earliestTime ? earliestTime.substring(0, 5) : startOfDay;
	}, [workplace, startOfDay, daysData, startOfWeek, businessWeekStart]);

	// console.log('workplaceWorkingHoursStart', workplaceWorkingHoursStart);
	const [filters, setFilters] = useState([
		{ id: 3, name: 'Pick', active: true },
		{ id: 4, name: 'Availability', active: true },
		{ id: 2, name: 'Time off', active: true },
		{ id: 5, name: 'Cover', active: true },
		{ id: 6, name: 'Trade', active: true },
	]);

	const setCalendarTypeHandler = (view: CalendarType) => {
		push(`?view=${view}&time=${timeView}`);
		setCalendarType(view);
	};

	// useCalendarView to decide whether we're in 'day' or 'week' view
	const { calendarView } = useCalendarView();

	// Filter shifts for day view - now shows all shifts in the week (same as week view)
	// Day view and week view both show the entire business week, just with different granularity

	// Create workplace users map by @id for efficient lookup
	const workplaceUsersMap = useMemo(() => {
		if (!workplaceUsers || !Array.isArray(workplaceUsers)) return new Map();
		return new Map(workplaceUsers.map((user) => [user['@id'], user]));
	}, [workplaceUsers]);
	// Generate resources based on calendar view
	const resourcesToPass = useMemo(() => {
		// Get shift requests from query client to avoid dependency issues
		const cachedShiftRequests = queryClient.getQueryData<any>([
			'shift-requests',
			workplaceId,
			canManageAllRequests,
		]);

		if (calendarView === 'day') {
			// Day view: Show all job codes as resources (even without shifts)
			if (!jobCodesData || jobCodesData.length === 0) return undefined;

			const jobCodeResources = jobCodesData.map((jobCode: any) => ({
				id: jobCode.id,
				name: jobCode.jobNumber
					? `${String(jobCode.jobNumber).padStart(2, '0')} ${jobCode.label}`
					: jobCode.label,
				background_color: jobCode.backgroundColor,
				text_color: jobCode.textColor,
				jobCode: jobCode,
			}));

			return jobCodeResources;
		}

		if (!allShifts.length) return undefined;
		if (calendarView === 'week' || calendarView === 'workweek') {
			// Week view: Show employees as resources with total hours
			const employeeResourcesMap = new Map();

			// Use fromDate and toDate from the calendar's current view for week boundaries
			// This ensures we're calculating hours for the exact 7-day week being displayed
			const weekStart = moment(fromDate).startOf('day');
			const weekEnd = moment(toDate).endOf('day');

			// Group shifts by employee and calculate total hours for the week
			allShifts.forEach((shift) => {
				const shiftStart = moment(shift.start);
				const shiftEnd = moment(shift.end);

				// Only include shifts within the business week (7 days from start_of_week)
				if (shiftStart.isBetween(weekStart, weekEnd, null, '[]')) {
					const employee = shift.employee;
					if (employee && employee.id) {
						const employeeKey = employee.id;
						const shiftDuration = moment.duration(shiftEnd.diff(shiftStart));
						const shiftHours = shiftDuration.asHours();

						if (!employeeResourcesMap.has(employeeKey)) {
							employeeResourcesMap.set(employeeKey, {
								id: employee.id,
								name: `${employee.firstName} ${employee.lastName}`,
								employee: employee,
								totalHours: shiftHours,
								shiftCount: 1,
							});
						} else {
							const existing = employeeResourcesMap.get(employeeKey);
							existing.totalHours += shiftHours;
							existing.shiftCount += 1;
						}
					} else {
						// Handle house shifts (shifts without assigned employees)
						const openShiftKey = 'open-shifts';
						const shiftDuration = moment.duration(shiftEnd.diff(shiftStart));
						const shiftHours = shiftDuration.asHours();

						if (!employeeResourcesMap.has(openShiftKey)) {
							employeeResourcesMap.set(openShiftKey, {
								id: openShiftKey,
								name: 'House Shifts',
								employee: null,
								totalHours: shiftHours,
								shiftCount: 1,
								isOpenShift: true,
							});
						} else {
							const existing = employeeResourcesMap.get(openShiftKey);
							existing.totalHours += shiftHours;
							existing.shiftCount += 1;
						}
					}
				}
			});

			// Convert map to array and add display names with hours, overtime, and minor indicators
			const employeeResources = Array.from(employeeResourcesMap.values()).map((resource) => {
				// Weekly overtime thresholds
				const weeklyOTThreshold = generalSettings?.overtimeWeeklyHoursAt || 40;
				const weeklyOTWarning = generalSettings?.overtimeWeeklyWarningAt || 40; // Use the actual setting value, not calculated
				const hasWeeklyOT = resource.totalHours > weeklyOTThreshold;
				const isApproachingWeeklyOT = !hasWeeklyOT && resource.totalHours >= weeklyOTWarning;
				const weeklyOTHours = hasWeeklyOT ? resource.totalHours - weeklyOTThreshold : 0;

				// Daily overtime thresholds - check each shift for this employee
				const dailyOTThreshold = generalSettings?.overtimeDailyHoursAt || 8;
				const dailyOTWarning = generalSettings?.overtimeDailyWarningAt || dailyOTThreshold * 0.9;

				// Find shifts for this employee within the selected week and check for daily overtime
				const employeeShifts = allShifts.filter((shift) => {
					if (shift.employee?.id !== resource.employee?.id) return false;
					const shiftStart = moment(shift.start);
					return shiftStart.isBetween(weekStart, weekEnd, null, '[]');
				});

				let hasDailyOT = false;
				let isApproachingDailyOT = false;
				let maxDailyHours = 0;

				// Group shifts by day and calculate hours per day
				const shiftsByDay = new Map<string, number>();
				employeeShifts.forEach((shift) => {
					const shiftStart = moment(shift.start);
					const shiftEnd = moment(shift.end);
					const dayKey = shiftStart.format('YYYY-MM-DD');

					const shiftDuration = moment.duration(shiftEnd.diff(shiftStart));
					const shiftHours = shiftDuration.asHours();

					const currentDayHours = shiftsByDay.get(dayKey) || 0;
					const newDayHours = currentDayHours + shiftHours;
					shiftsByDay.set(dayKey, newDayHours);

					if (newDayHours > maxDailyHours) {
						maxDailyHours = newDayHours;
					}

					if (newDayHours > dailyOTThreshold) {
						hasDailyOT = true;
					} else if (newDayHours > dailyOTWarning) {
						isApproachingDailyOT = true;
					}
				});
				// console.log(
				// 	'daily OT for ',
				// 	resource.name,
				// 	shiftsByDay,
				// 	maxDailyHours,
				// 	hasDailyOT,
				// 	isApproachingDailyOT
				// );

				// Determine overall overtime status (daily takes precedence over weekly)
				const hasOT = hasDailyOT || hasWeeklyOT;

				// Calculate total overtime hours for display
				const otHours = hasDailyOT ? maxDailyHours - dailyOTThreshold : weeklyOTHours;

				// Check for minor age using workplace users map
				const minorAge = generalSettings?.ageOfMinor || 18;
				const workplaceUser = workplaceUsersMap.get(resource.employee?.['@id']);
				const employeeDOB = workplaceUser?.dateOfBirth;
				const isMinor = employeeDOB
					? moment().diff(moment(employeeDOB), 'years') < minorAge
					: false;

				// Check for availability and time-off conflicts using user data
				const employeeRequests =
					cachedShiftRequests?.member?.filter(
						(req: any) => req.employeeRequest?.id === resource.employee?.id
					) || [];

				// Check if employee has time off from userTimeOff field
				const hasTimeOff = (workplaceUser?.userTimeOff || []).some((timeOff: any) => {
					if (timeOff.startAt && timeOff.endAt) {
						const timeOffStart = moment(`${timeOff.startAt}.000Z`);
						const timeOffEnd = moment(`${timeOff.endAt}.000Z`);
						return (
							timeOffStart.isBetween(weekStart, weekEnd, 'day', '[]') ||
							timeOffEnd.isBetween(weekStart, weekEnd, 'day', '[]') ||
							(timeOffStart.isSameOrBefore(weekStart) && timeOffEnd.isSameOrAfter(weekEnd))
						);
					}
					return false;
				});

				// Check if employee has availability issues from userAvailability field
				const hasAvailabilityIssue = (workplaceUser?.userAvailability || []).some((avail: any) => {
					if (avail.startAt && avail.endAt) {
						const availStart = moment(`${avail.startAt}.000Z`);
						const availEnd = moment(`${avail.endAt}.000Z`);
						return (
							availStart.isBetween(weekStart, weekEnd, 'day', '[]') ||
							availEnd.isBetween(weekStart, weekEnd, 'day', '[]') ||
							(availStart.isSameOrBefore(weekStart) && availEnd.isSameOrAfter(weekEnd))
						);
					}
					return false;
				});

				// Get availability data for the current week
				const userAvailabilityThisWeek = (workplaceUser?.userAvailability || []).filter(
					(avail: any) => {
						if (avail.startAt && avail.endAt) {
							const availStart = moment(`${avail.startAt}.000Z`);
							const availEnd = moment(`${avail.endAt}.000Z`);
							return (
								availStart.isBetween(weekStart, weekEnd, 'day', '[]') ||
								availEnd.isBetween(weekStart, weekEnd, 'day', '[]') ||
								(availStart.isSameOrBefore(weekStart) && availEnd.isSameOrAfter(weekEnd))
							);
						}
						return false;
					}
				);
				// Build display name with indicators
				const statusParts = [];
				if (hasOT) {
					if (hasDailyOT) {
						const dailyOTHours = maxDailyHours - dailyOTThreshold;
						statusParts.push(`Daily OT: ${dailyOTHours.toFixed(1)}h`);
					} else if (hasWeeklyOT) {
						const weeklyOTHours = resource.totalHours - weeklyOTThreshold;
						statusParts.push(`Weekly OT: ${weeklyOTHours.toFixed(1)}h`);
					}
				}
				if (isMinor) {
					statusParts.push('Minor');
				}
				const displaySuffix =
					statusParts.length > 0
						? ` (${resource.totalHours.toFixed(1)}h, ${statusParts.join(', ')})`
						: ` (${resource.totalHours.toFixed(1)}h)`;

				return {
					...resource,
					name: resource.isOpenShift
						? `${resource.name} (${resource.totalHours.toFixed(1)}h)`
						: `${resource.name}${displaySuffix}`,
					displayName: resource.name, // Keep original name for display
					hasOvertime: hasOT,
					isApproachingDailyOvertime: isApproachingDailyOT && !hasOT,
					isApproachingWeeklyOvertime: isApproachingWeeklyOT && !hasOT,
					overtimeHours: otHours,
					hasDailyOvertime: hasDailyOT,
					hasWeeklyOvertime: hasWeeklyOT,
					maxDailyHours: maxDailyHours,
					dailyOTWarning: dailyOTWarning,
					weeklyOTWarning: weeklyOTWarning,
					isMinor: isMinor,
					hasTimeOff: hasTimeOff,
					hasAvailabilityIssue: hasAvailabilityIssue,
					userAvailability: userAvailabilityThisWeek,
					employeeRequests: employeeRequests,
				};
			}); // Sort by name (house shifts last)
			employeeResources.sort((a, b) => {
				if (a.isOpenShift && !b.isOpenShift) return 1;
				if (!a.isOpenShift && b.isOpenShift) return -1;
				return a.displayName.localeCompare(b.displayName);
			});

			return employeeResources;
		}

		return undefined;
	}, [
		calendarView,
		allShifts,
		generalSettings,
		fromDate,
		toDate,
		jobCodesData,
		queryClient,
		workplaceId,
		canManageAllRequests,
		workplaceUsersMap,
	]);

	// Determine the selected date for the calendar
	// Use lastFocusedDate if available to maintain scroll position, otherwise use businessWeekStart
	const calendarSelectedDate = useMemo(() => {
		if (!businessWeekStart) return undefined;

		// If we have a lastFocusedDate (from user interaction), use that to maintain scroll position
		if (lastFocusedDate) {
			// Verify that lastFocusedDate is within the current business week
			// to prevent navigation to a different week
			const weekStartDate = moment(businessWeekStart).startOf('day');
			const weekEndDate = weekStartDate.clone().add(6, 'days').endOf('day');
			const focusedDateMoment = moment(lastFocusedDate);
			// console.log("'calendarSelectedDate' check lastFocusedDate", {
			// 	lastFocusedDate,
			// 	weekStartDate: weekStartDate.format(),
			// 	weekEndDate: weekEndDate.format(),
			// 	isInWeek: focusedDateMoment.isBetween(weekStartDate, weekEndDate, null, '[]'),
			// });
			// Only use lastFocusedDate if it's within the current business week
			if (focusedDateMoment.isBetween(weekStartDate, weekEndDate, null, '[]')) {
				return moment(lastFocusedDate).toDate();
			}

			// If lastFocusedDate is outside the current week, fall through to use businessWeekStart
		}

		// Parse the working hours start time (format: "HH:mm")
		const workingHoursTime = workplaceWorkingHoursStart || startOfDay || '00:00';
		const [hours, minutes] = workingHoursTime.split(':').map(Number);

		// Use businessWeekStart directly as the selected date
		const selectedDate = moment(businessWeekStart).startOf('day');

		// Set the time to workplace working hours start for scroll position
		selectedDate.set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });

		return selectedDate.toDate();
	}, [businessWeekStart, workplaceWorkingHoursStart, startOfDay, lastFocusedDate]);

	// Fetch all scheduler metrics (will be filtered by business week in calculateAggregateMetric)
	const { data: allMetricsData, refetch: _refetchMetrics } = useQuery(
		['scheduler-metrics', workplaceId],
		() => {
			if (!workplaceId) return Promise.resolve([]);
			return getSchedulerMetrics(workplaceId);
		},
		{
			enabled: !!workplaceId,
			// refetchOnWindowFocus: true,
		}
	);

	// Filter metrics data based on current business week range
	const weekMetricsData = useMemo(() => {
		if (!allMetricsData || !Array.isArray(allMetricsData) || !fromDate || !toDate) return [];

		const weekStart = moment(fromDate).format('YYYY-MM-DD');
		const weekEnd = moment(toDate).format('YYYY-MM-DD');

		return allMetricsData.filter((metric: any) => {
			const metricDay = metric.day;
			return metricDay >= weekStart && metricDay <= weekEnd;
		});
	}, [allMetricsData, fromDate, toDate]);

	// Calculate schedule statistics
	const scheduleStats = useMemo(() => {
		// Get business week boundaries based on businessWeekStart from context
		let weekStartDate: moment.Moment;
		let weekEndDate: moment.Moment;

		if (businessWeekStart) {
			// Use businessWeekStart from context
			weekStartDate = moment(businessWeekStart);
			weekEndDate = weekStartDate.clone().add(6, 'days').endOf('day');
		} else {
			// Fallback: calculate from fromDate and startOfWeek
			const dayMap: { [key: string]: number } = {
				Sunday: 0,
				Monday: 1,
				Tuesday: 2,
				Wednesday: 3,
				Thursday: 4,
				Friday: 5,
				Saturday: 6,
			};

			const startDayOfWeek = dayMap[startOfWeek] ?? 1;
			const currentDate = moment(fromDate);
			const currentDayOfWeek = currentDate.day();

			let daysBack = currentDayOfWeek - startDayOfWeek;
			if (daysBack < 0) {
				daysBack += 7;
			}

			weekStartDate = currentDate.clone().subtract(daysBack, 'days').startOf('day');
			weekEndDate = weekStartDate.clone().add(6, 'days').endOf('day');
		}

		// Filter shifts to only include those within the business week
		const weekShifts = allShifts.filter((shift) => {
			const shiftStart = moment(shift.start);
			return shiftStart.isBetween(weekStartDate, weekEndDate, null, '[]');
		});

		// Calculate total hours scheduled based on current view
		let totalHours = 0;

		if (calendarView === 'day') {
			// For day view, only count hours for the selected day
			const currentDay = moment(calendarSelectedDate).startOf('day');
			const currentDayEnd = moment(calendarSelectedDate).endOf('day');

			totalHours = weekShifts.reduce((total, shift) => {
				const shiftStart = moment(shift.start);
				// Only include shifts that occur on the selected day
				if (shiftStart.isBetween(currentDay, currentDayEnd, null, '[]')) {
					const start = moment(shift.start);
					const end = moment(shift.end);
					const hours = end.diff(start, 'hours', true);
					return total + hours;
				}
				return total;
			}, 0);
		} else {
			// For week and workweek views, count hours for the entire week
			totalHours = weekShifts.reduce((total, shift) => {
				const start = moment(shift.start);
				const end = moment(shift.end);
				const hours = end.diff(start, 'hours', true);
				return total + hours;
			}, 0);
		}

		// Calculate daily OT and weekly OT for the entire week
		const dailyOTHoursThreshold = generalSettings?.overtimeDailyHoursAt || 8;
		const weeklyOTHoursThreshold = generalSettings?.overtimeWeeklyHoursAt || 40;

		const employeeDailyHoursMap = new Map<string, Map<string, number>>(); // employeeId -> (date -> hours)
		const employeeWeeklyHoursMap = new Map<string, number>(); // employeeId -> total weekly hours

		weekShifts.forEach((shift) => {
			if (shift.employee) {
				const employeeId = shift.employee.id?.toString() || shift.employee.toString();
				const shiftStart = moment(shift.start);
				const shiftEnd = moment(shift.end);
				const shiftDate = shiftStart.format('YYYY-MM-DD');
				const shiftHours = shiftEnd.diff(shiftStart, 'hours', true);

				// Track daily hours per employee
				if (!employeeDailyHoursMap.has(employeeId)) {
					employeeDailyHoursMap.set(employeeId, new Map<string, number>());
				}
				const employeeDailyHours = employeeDailyHoursMap.get(employeeId)!;
				employeeDailyHours.set(shiftDate, (employeeDailyHours.get(shiftDate) || 0) + shiftHours);

				// Track total weekly hours per employee
				employeeWeeklyHoursMap.set(
					employeeId,
					(employeeWeeklyHoursMap.get(employeeId) || 0) + shiftHours
				);
			}
		});

		// Calculate daily OT as the weekly total of all daily OT during the week
		let dailyOT = 0;
		employeeDailyHoursMap.forEach((dailyHours) => {
			dailyHours.forEach((hoursWorked) => {
				if (hoursWorked > dailyOTHoursThreshold) {
					dailyOT += hoursWorked - dailyOTHoursThreshold;
				}
			});
		});

		// Calculate weekly OT as the weekly total of all OT that week (hours over weekly threshold)
		let weeklyOT = 0;
		employeeWeeklyHoursMap.forEach((weeklyHours) => {
			if (weeklyHours > weeklyOTHoursThreshold) {
				weeklyOT += weeklyHours - weeklyOTHoursThreshold;
			}
		});

		return {
			sales: 0, // Will be updated below
			splh: 0,
			cplh: 0,
			hoursScheduled: totalHours,
			dailyOT: dailyOT,
			weeklyOT: weeklyOT,
			customers: 0,
		};
	}, [
		allShifts,
		generalSettings,
		businessWeekStart,
		fromDate,
		startOfWeek,
		calendarView,
		calendarSelectedDate,
	]);

	// Helper function to calculate aggregated metrics from scheduler metrics
	const calculateAggregateMetric = useCallback(
		(metricKey: 'sales' | 'customers' | 'splh' | 'cplh'): number => {
			if (!weekMetricsData || !Array.isArray(weekMetricsData)) return 0;

			// Always show weekly totals for sales, customers, SPLH, and CPLH
			// SPLH = Total Sales / Total Hours Scheduled for the week
			if (metricKey === 'splh') {
				const totalSales = weekMetricsData.reduce(
					(total: number, m: any) => total + (m.sales || 0),
					0
				);
				const totalHours = weekMetricsData.reduce(
					(total: number, m: any) => total + (m.hoursScheduled || 0),
					0
				);
				return totalHours > 0 ? totalSales / totalHours : 0;
			}

			// CPLH = Total Customers / Total Hours Scheduled for the week
			if (metricKey === 'cplh') {
				const totalCustomers = weekMetricsData.reduce(
					(total: number, m: any) => total + (m.customers || 0),
					0
				);
				const totalHours = weekMetricsData.reduce(
					(total: number, m: any) => total + (m.hoursScheduled || 0),
					0
				);
				return totalHours > 0 ? totalCustomers / totalHours : 0;
			}

			// Sum sales and customers across all days for the week
			return weekMetricsData.reduce((total: number, m: any) => total + (m[metricKey] || 0), 0);
		},
		[weekMetricsData]
	);

	// Update schedule stats with metrics data
	const statsWithMetrics = useMemo(() => {
		return {
			...scheduleStats,
			sales: calculateAggregateMetric('sales'),
			splh: calculateAggregateMetric('splh'),
			cplh: calculateAggregateMetric('cplh'),
			customers: calculateAggregateMetric('customers'),
		};
	}, [scheduleStats, calculateAggregateMetric]);
	// console.log('schedule stats with metrics', statsWithMetrics, scheduleStats);
	const { mutate: _updateOneShift, isLoading: _loadingUpdateOneShift } = useMutation(PutOneShift);

	const { mutate: updateShiftOnDrag } = useMutation(
		(params: { id: number; body: IShiftPayload }) =>
			PutOneShift({ id: params.id, body: params.body }),
		{
			onSuccess: async (data: any, variables: any) => {
				showToast('success', 'Shift updated successfully!');
				queryClient.invalidateQueries(['shifts']);
				refetchShifts();

				// Update scheduler metrics for the affected day
				try {
					const shiftDate = variables.body.startAt
						? moment(variables.body.startAt).format('YYYY-MM-DD')
						: null;
					if (shiftDate && workplaceId) {
						// Fetch existing metrics for this day
						const existingMetrics = await getSchedulerMetrics(workplaceId, shiftDate, shiftDate);
						if (existingMetrics && existingMetrics.length > 0) {
							const metric = existingMetrics[0];
							// Calculate new hours scheduled from all shifts for this day
							const dayShifts = allShifts.filter(
								(shift: any) => moment(shift.startAt).format('YYYY-MM-DD') === shiftDate
							);
							let totalHours = 0;
							let otHours = 0;
							dayShifts.forEach((shift: any) => {
								const duration = moment.duration(moment(shift.endAt).diff(moment(shift.startAt)));
								const hours = duration.asHours();
								if (hours > 8) {
									otHours += hours - 8;
									totalHours += 8;
								} else {
									totalHours += hours;
								}
							});
							// Update metrics with new hours scheduled
							await updateSchedulerMetric(metric.id, {
								workplace: metric.workplace,
								day: metric.day,
								sales: metric.sales,
								customers: metric.customers,
								hoursScheduled: totalHours,
								otHoursScheduled: otHours,
							});
						}
					}
				} catch (error) {
					console.error('Error updating scheduler metrics:', error);
					// Don't show error to user as the shift was already updated
				}
			},
			onError: (error: any) => {
				showToast('error', error?.message || 'Failed to update shift');
				refetchShifts(); // Refetch to revert UI to correct state
			},
		}
	);

	const { mutate: deleteShift, isLoading: isDeleteLoading } = useMutation(DeleteShift, {
		onSuccess: () => {
			showToast('success', 'Shift deleted successfully!');
			queryClient.invalidateQueries(['shifts']);
			refetchShifts();
			setIsDeleteDialogOpen(false);
			setShiftToDelete(null);
		},
		onError: (error: any) => {
			showToast('error', error?.message || 'Failed to delete shift');
		},
	});

	const { mutate: copyShift } = useMutation(PostShifts, {
		onSuccess: () => {
			showToast('success', 'Shift copied successfully!');
			queryClient.invalidateQueries(['shifts']);
			refetchShifts();
		},
		onError: (error: any) => {
			showToast('error', error?.message || 'Failed to copy shift');
		},
	});

	const { mutate: publishShifts, isLoading: _isPublishLoading } = useMutation(
		publishShiftsInRange,
		{
			onSuccess: () => {
				showToast('success', 'Shifts published successfully!');
				queryClient.invalidateQueries(['shifts']);
				refetchShifts();
			},
			onError: (error: any) => {
				showToast('error', error?.message || 'Failed to publish shifts');
			},
		}
	);

	// Fetch shift requests
	// If user has execute permission on 'Schedule - All', fetch all requests
	// Fetch all shift requests (filtering done on frontend based on permission)
	const {
		data: shiftRequestsData,
		refetch: refetchShiftRequests,
		isLoading: isLoadingRequests,
	} = useQuery(
		['shift-requests', workplaceId, canManageAllRequests],
		() => {
			const params: any = { workplace_id: workplaceId, limit: 100 };
			// API does not support employee_id filtering, so fetch all and filter on frontend
			return getShiftRequests(params);
		},
		{
			enabled: !!workplaceId,
			onError: (error: any) => {
				showToast('error', error?.message || 'Failed to fetch shift requests');
			},
		}
	);

	// Fetch shift request types and cache them (called once on SchedulePage load)
	useQuery(['shift-request-types'], getShiftRequestTypes, {
		onSuccess: (data) => {
			// Cache the types when successfully fetched
			if (data?.['member']) {
				cacheShiftRequestTypes(data['member']);
			}
		},
		onError: (error: any) => {
			showToast('error', error?.message || 'Failed to fetch request types');
		},
		staleTime: Infinity,
		cacheTime: Infinity,
	});

	// Delete shift request mutation
	const { mutate: deleteShiftRequestMutation, isLoading: isDeletingRequest } = useMutation(
		deleteShiftRequest,
		{
			onSuccess: () => {
				showToast('success', 'Shift request deleted successfully!');
				queryClient.invalidateQueries(['shift-requests']);
				refetchShiftRequests();
				setDeleteRequestConfirmOpen(false);
				setRequestToDelete(null);
			},
			onError: (error: any) => {
				showToast('error', error?.message || 'Failed to delete shift request');
			},
		}
	);

	// Handler for opening publish modal
	const handleOpenPublishModal = () => {
		setOpenPublishModal(true);
	};

	// Handler for publishing shifts
	const handlePublishShifts = (startDate: string, endDate: string) => {
		if (!workplaceId) return;

		publishShifts({
			workplace: `/api/workplaces/${workplaceId}`,
			start: startDate,
			end: endDate,
		});

		setOpenPublishModal(false);
	};

	const { data: _eventTypes } = useQuery('eventTypes', () => fetchAllEventTypes(), {
		staleTime: 300000,
	});

	const _eventTypesMap = useMemo(() => {
		if (!_eventTypes?.member) return {};
		return _eventTypes.member.reduce((map: any, eventType: any) => {
			map[eventType['@id']] = eventType;
			return map;
		}, {});
	}, [_eventTypes]);

	// Fetch shifts data
	const { refetch: refetchShifts, isLoading: isLoadingShifts } = useQuery(
		['shifts', workplaceId, fromDate, toDate],
		() =>
			fetchAllShifts({
				workplaceId,
				fromDate,
				toDate,
			}),
		{
			refetchInterval: 600000,
			enabled: !!workplaceId && !!fromDate && !!toDate,
			onSuccess: (data) => {
				const shifts = data?.member || [];
				const currentUserId = localStorage.getItem('userId');

				let filteredShifts = shifts;

				if (!canViewAllSchedules) {
					// Regular employees: only see their own shifts and house shifts (published or created by them)
					if (currentUserId) {
						filteredShifts = shifts.filter((shift: Shift) => {
							// For house shifts (unassigned), check published status
							if (!shift.employee || shift.employee === null || shift.employee === undefined) {
								// Show published house shifts to everyone
								if (shift.published) {
									return true;
								}
								// Show unpublished house shifts only if created by current user
								const creatorId = shift.createdBy?.id?.toString() || shift.createdBy?.toString();
								return creatorId === currentUserId;
							}

							// Show unpublished shifts only if created by current user
							if (!shift.published) {
								const creatorId = shift.createdBy?.id?.toString() || shift.createdBy?.toString();
								return creatorId === currentUserId;
							}

							// Show published shifts if assigned to current user
							const employeeId =
								shift.employee?.id?.toString() ||
								shift.employee?.toString().replace('/api/employees/', '') ||
								shift.employee?.toString();

							return employeeId === currentUserId;
						});
					}
				} else {
					// Users with view all or publish permission: see all published shifts and only unpublished shifts they created
					if (currentUserId) {
						filteredShifts = shifts.filter((shift: Shift) => {
							if (shift.published) {
								return true;
							}
							// For house shifts (unassigned), check published status
							if (!shift.employee || shift.employee === null || shift.employee === undefined) {
								// Show unpublished house shifts only if created by current user
								const creatorId = shift.createdBy?.id?.toString() || shift.createdBy?.toString();
								return creatorId === currentUserId;
							}

							// Show all published shifts

							// Show unpublished shifts only if created by current user
							const creatorId = shift.createdBy?.id?.toString() || shift.createdBy?.toString();
							return creatorId === currentUserId;
						});
					}
				}

				// Calculate employee status data for each shift individually
				// (we need to determine which specific shift causes overtime)

				const calculateEmployeeStatus = (employeeId: number, currentShift: Shift) => {
					// Don't use cache - we need to calculate for each shift individually
					// since each shift needs to know if IT is the one causing overtime

					// Determine the current business week range
					const weekStart = moment(fromDate);
					const weekEnd = moment(toDate);

					// For day view, get the selected date to calculate daily totals
					const selectedDayKey =
						calendarView === 'day' ? moment(calendarSelectedDate).format('YYYY-MM-DD') : null;

					// Find all shifts for this employee within the current business week
					const employeeShifts = filteredShifts.filter((s: Shift) => {
						if (s.employee?.id !== employeeId) return false;
						const shiftStart = moment(`${s.startAt}.000Z`);
						return shiftStart.isBetween(weekStart, weekEnd, null, '[]');
					});

					// Calculate total weekly hours
					let totalHours = 0;
					employeeShifts.forEach((s: Shift) => {
						const duration = moment.duration(moment(s.endAt).diff(moment(s.startAt)));
						totalHours += duration.asHours();
					});

					// Weekly overtime thresholds
					const weeklyOTThreshold = generalSettings?.overtimeWeeklyHoursAt || 40;
					const weeklyOTWarning = generalSettings?.overtimeWeeklyWarningAt || 40; // Use the actual setting value, not calculated
					const hasWeeklyOT = totalHours > weeklyOTThreshold;
					const isApproachingWeeklyOT = !hasWeeklyOT && totalHours >= weeklyOTWarning;
					const dailyOTThreshold = generalSettings?.overtimeDailyHoursAt || 8;
					const dailyOTWarning = generalSettings?.overtimeDailyWarningAt || dailyOTThreshold * 0.9;

					let hasDailyOT = false;
					let isApproachingDailyOT = false;
					let maxDailyHours = 0;
					let todayHours = 0;
					let todayOvertimeHours = 0;

					// Track which shift causes daily overtime
					let isShiftCausingDailyOT = false;

					// Group shifts by day and determine which shift causes daily OT
					const shiftsByDay = new Map<string, number>();
					const shiftsByDayArray = new Map<string, Shift[]>();

					employeeShifts.forEach((s: Shift) => {
						const dayKey = moment(s.startAt).format('YYYY-MM-DD');
						if (!shiftsByDayArray.has(dayKey)) {
							shiftsByDayArray.set(dayKey, []);
						}
						shiftsByDayArray.get(dayKey)!.push(s);
					});

					// Sort shifts within each day by start time
					shiftsByDayArray.forEach((shifts, dayKey) => {
						shifts.sort((a, b) => moment(a.startAt).valueOf() - moment(b.startAt).valueOf());

						let dailyAccumulator = 0;
						for (const s of shifts) {
							const duration = moment.duration(moment(s.endAt).diff(moment(s.startAt)));
							const hours = duration.asHours();
							const prevHours = dailyAccumulator;
							dailyAccumulator += hours;
							// Check if THIS shift is the one that pushes into daily OT
							if (s.id === currentShift.id && dailyAccumulator > dailyOTThreshold) {
								isShiftCausingDailyOT = true;
							}
						}

						shiftsByDay.set(dayKey, dailyAccumulator);

						// Track the hours for the selected day (for day view)
						if (selectedDayKey && dayKey === selectedDayKey) {
							todayHours = dailyAccumulator;
						}

						if (dailyAccumulator > maxDailyHours) {
							maxDailyHours = dailyAccumulator;
						}

						if (dailyAccumulator > dailyOTThreshold) {
							hasDailyOT = true;
						} else if (dailyAccumulator > dailyOTWarning) {
							isApproachingDailyOT = true;
						}
					});

					// Calculate overtime for the specific day (for day view)
					if (todayHours > dailyOTThreshold) {
						todayOvertimeHours = todayHours - dailyOTThreshold;
					}

					// Determine which shift causes weekly overtime
					let isShiftCausingWeeklyOT = false;
					if (hasWeeklyOT) {
						// Sort all shifts by start time
						const sortedShifts = [...employeeShifts].sort(
							(a, b) => moment(a.startAt).valueOf() - moment(b.startAt).valueOf()
						);

						let weeklyAccumulator = 0;
						for (const s of sortedShifts) {
							const duration = moment.duration(moment(s.endAt).diff(moment(s.startAt)));
							const hours = duration.asHours();
							const prevWeeklyHours = weeklyAccumulator;
							weeklyAccumulator += hours;

							// Check if THIS shift is the one that pushes into weekly OT
							if (
								s.id === currentShift.id &&
								prevWeeklyHours < weeklyOTThreshold &&
								weeklyAccumulator > weeklyOTThreshold
							) {
								isShiftCausingWeeklyOT = true;
								break;
							}
						}
					}

					const hasOT = hasDailyOT || hasWeeklyOT;

					// For day view, use today's overtime; for week view, use the max daily or weekly
					const otHours = hasDailyOT
						? maxDailyHours - dailyOTThreshold
						: totalHours - weeklyOTThreshold;

					// Check for minor
					const minorAge = generalSettings?.ageOfMinor || 18;
					const workplaceUser = workplaceUsersMap.get(`/api/users/${employeeId}`);
					const employeeDOB = workplaceUser?.dateOfBirth;
					const isMinor = employeeDOB
						? moment().diff(moment(employeeDOB), 'years') < minorAge
						: false;

					// Check if employee has time off from userTimeOff field
					const hasTimeOff = (workplaceUser?.userTimeOff || []).some((timeOff: any) => {
						if (timeOff.startAt && timeOff.endAt) {
							const timeOffStart = moment(`${timeOff.startAt}.000Z`);
							const timeOffEnd = moment(`${timeOff.endAt}.000Z`);
							const weekStart = moment(fromDate);
							const weekEnd = moment(toDate);
							return (
								timeOffStart.isBetween(weekStart, weekEnd, 'day', '[]') ||
								timeOffEnd.isBetween(weekStart, weekEnd, 'day', '[]') ||
								(timeOffStart.isSameOrBefore(weekStart) && timeOffEnd.isSameOrAfter(weekEnd))
							);
						}
						return false;
					});

					// Check if employee has availability conflicts with this specific shift
					// Availability is a recurring weekly pattern (e.g., available Mon 12pm-8pm)
					let hasAvailabilityIssue = false;
					let availabilityMessage = null;

					if (
						workplaceUser?.userAvailability &&
						workplaceUser.userAvailability.length > 0 &&
						daysData
					) {
						const shiftStart = moment(currentShift.startAt);
						const shiftEnd = moment(currentShift.endAt);
						const shiftDayOfWeek = shiftStart.day(); // 0 = Sunday, 1 = Monday, etc.
						const shiftStartTimeOnly = shiftStart.format('HH:mm:ss');
						const shiftEndTimeOnly = shiftEnd.format('HH:mm:ss');
						const dayNames = [
							'Sunday',
							'Monday',
							'Tuesday',
							'Wednesday',
							'Thursday',
							'Friday',
							'Saturday',
						];
						const shiftDayName = dayNames[shiftDayOfWeek];

						// console.log('[Availability Check]', {
						// 	employeeId,
						// 	shiftId: currentShift.id,
						// 	shiftDayName,
						// 	shiftStartTimeOnly,
						// 	shiftEndTimeOnly,
						// 	userAvailability: workplaceUser.userAvailability,
						// });

						// Find availability for this shift's day of week
						const dayAvailabilities = workplaceUser.userAvailability.filter((avail: any) => {
							if (!avail.day) return false;
							const dayId = typeof avail.day === 'string' ? avail.day : avail.day['@id'];
							const dayData = daysData?.find(
								(d: any) => d['@id'] === dayId || `/api/days/${d.id}` === dayId
							);
							const matches = dayData && dayData.label === shiftDayName;
							return matches;
						});

						if (dayAvailabilities.length > 0) {
							// Check if shift time falls within any availability window
							const isWithinAvailability = dayAvailabilities.some((avail: any) => {
								if (!avail.startAt || !avail.endAt) return false;

								// Parse availability times to extract time component only
								const availStartTimeOnly = moment(avail.startAt).format('HH:mm:ss');
								const availEndTimeOnly = moment(avail.endAt).format('HH:mm:ss');

								const isWithin =
									shiftStartTimeOnly >= availStartTimeOnly && shiftEndTimeOnly <= availEndTimeOnly;

								return isWithin;
							});

							if (!isWithinAvailability) {
								hasAvailabilityIssue = true;
								// Build availability message
								const availTimes = dayAvailabilities
									.map(
										(avail: any) =>
											`${moment(avail.startAt).format('h:mm A')} - ${moment(avail.endAt).format('h:mm A')}`
									)
									.join(', ');
								availabilityMessage = `Available on ${shiftDayName}: ${availTimes}`;
							}
						} else {
							// console.log('[No availability set for this day]', shiftDayName);
						}
					}

					const statusData = {
						hasOvertime: hasOT,
						isApproachingDailyOvertime: isApproachingDailyOT && !hasOT,
						isApproachingWeeklyOvertime: isApproachingWeeklyOT && !hasOT,
						overtimeHours: otHours,
						todayOvertimeHours: todayOvertimeHours, // Overtime for the specific day
						todayHours: todayHours, // Total hours for the specific day
						hasDailyOvertime: hasDailyOT,
						hasWeeklyOvertime: hasWeeklyOT,
						maxDailyHours: maxDailyHours,
						totalHours: totalHours,
						isMinor: isMinor,
						hasTimeOff: hasTimeOff,
						hasAvailabilityIssue: hasAvailabilityIssue,
						availabilityMessage: availabilityMessage,
						isShiftCausingDailyOT: isShiftCausingDailyOT,
						isShiftCausingWeeklyOT: isShiftCausingWeeklyOT,
						dailyOTWarning: dailyOTWarning,
						weeklyOTWarning: weeklyOTWarning,
					};

					return statusData;
				};

				setAllShifts(
					filteredShifts.map((shift: Shift): CalendarShift => {
						// Get employee data from workplaceUsersMap
						const employeeData = shift.employee?.['@id']
							? workplaceUsersMap.get(shift.employee['@id'])
							: null;
						// console.log("workplace USER in day view", employeeData, calendarView )

						// Calculate isMinor for the employee
						const minorAge = generalSettings?.ageOfMinor || 18;
						let isMinor = false;
						if (employeeData?.dateOfBirth) {
							const age = moment().diff(moment(employeeData.dateOfBirth), 'years');
							isMinor = age < minorAge;
						}

						// Enhance employee object with isMinor property
						const enhancedEmployee = shift.employee
							? {
									...shift.employee,
									isMinor: isMinor,
									dateOfBirth: employeeData?.dateOfBirth,
								}
							: shift.employee;

						const calendarShift: CalendarShift = {
							...shift,
							employee: enhancedEmployee,
							id: shift.id,
							// Use shift times as-is without timezone conversion
							start: moment(shift.startAt).toDate(),
							end: moment(shift.endAt).toDate(),
							shift_id: shift.id,
							editable: canManageShifts,
							role: shift.jobCode?.label,
							background_color: shift.jobCode?.backgroundColor,
							text_color: shift.jobCode?.textColor,
							color: shift.jobCode?.backgroundColor,
							published: shift.published,
							publishedAt: shift.publishedAt,
							publishedBy: shift.publishedBy,
							createdBy: shift.createdBy,
							employeeStatusData: shift.employee?.id
								? calculateEmployeeStatus(shift.employee.id, shift)
								: null,
							// Include employee's timeoff and availability data
							employeeTimeOff: employeeData?.userTimeOff || [],
							employeeAvailability: employeeData?.userAvailability || [],
						};
						return calendarShift;
					})
				);
			},
			onError: (error: any) => {
				showToast('error', error?.message || 'Failed to fetch shifts');
			},
		}
	);

	// Check if there are any unpublished shifts in the current business week to enable/disable publish button
	const hasUnpublishedShifts = useMemo(() => {
		if (!fromDate || !startOfWeek) return false;

		const dayMap: { [key: string]: number } = {
			Sunday: 0,
			Monday: 1,
			Tuesday: 2,
			Wednesday: 3,
			Thursday: 4,
			Friday: 5,
			Saturday: 6,
		};

		const startDayOfWeek = dayMap[startOfWeek] ?? 1;
		const currentDate = moment(fromDate);
		const currentDayOfWeek = currentDate.day();

		let daysBack = currentDayOfWeek - startDayOfWeek;
		if (daysBack < 0) {
			daysBack += 7;
		}

		const businessWeekStart = currentDate.clone().subtract(daysBack, 'days').startOf('day');
		const businessWeekEnd = businessWeekStart.clone().add(6, 'days').endOf('day');

		// Check if any unpublished shift falls within the current business week
		return allShifts.some((shift) => {
			if (shift.published) return false;
			const shiftStart = moment(shift.startAt);
			return shiftStart.isBetween(businessWeekStart, businessWeekEnd, null, '[]');
		});
	}, [allShifts, fromDate, startOfWeek]);

	const { refetch: refetchEvents, isLoading: isLoadingEvents } = useQuery(
		'events-schedule-page',
		() =>
			fetchAllEvents({
				workplaceId,
				fromDate: fromDate,
				toDate: toDate,
			}),
		{
			refetchInterval: 600000, // 10 min interval for refresh
			enabled: !!fromDate && !!toDate,
			onSuccess: async (data) => {
				// console.log('Fetched events data:', data);
				const events = data?.member || [];
				setAllEvents(
					events.map((event: any) => {
						return {
							...event,
							// Map API fields to expected format
							start: event.startDate,
							end: event.endDate,
							title: event.name,
							resource: event?.type,
							event_type: event?.type,
						};
					})
				);
			},
		}
	);

	const handleShiftDragUpdate = (event: any) => {
		// Check if user has permission to edit shifts
		if (!canManageShifts) {
			showToast('error', 'You do not have permission to edit shifts');
			return;
		}

		// Check if the new date is in the past
		if (isDateInPast(event.start)) {
			showToast('error', 'Cannot move shifts to past dates');
			return;
		}

		// Extract shift ID - could be in id or shift_id
		const shiftId = event.shift_id || event.id;
		if (!shiftId) {
			showToast('error', 'Invalid shift data');
			return;
		}

		// Build the payload for the API
		// event.start and event.end are Date objects - send as-is without timezone conversion
		const payload: IShiftPayload = {
			workplace: `api/workplaces/${workplaceId}`,
			startAt: moment(event.start).format('YYYY-MM-DDTHH:mm:ss'),
			endAt: moment(event.end).format('YYYY-MM-DDTHH:mm:ss'),
			employee:
				event.employee?.['@id'] || (event.employee?.id ? `api/users/${event.employee.id}` : null),
			jobCode:
				event.jobCode?.['@id'] || (event.jobCode?.id ? `api/job_codes/${event.jobCode.id}` : null),
		}; // Call the API to update the shift
		updateShiftOnDrag({ id: shiftId, body: payload });
	};

	const openEditModal = (event: any) => {
		// Check if user has permission to edit shifts
		if (calendarType === 'schedule' && !canManageShifts) {
			showToast('error', 'You do not have permission to edit shifts');
			return;
		}

		// Check if the shift date is in the past
		if (calendarType === 'schedule' && isDateInPast(event.start || event.startAt)) {
			showToast('error', 'Cannot edit shifts on past dates');
			return;
		}

		setEditEvent(event);

		// Set the date to scroll to when returning from edit
		if (event.start) {
			setLastFocusedDate(event.start);
		}

		switch (calendarType) {
			case 'schedule':
				setCurrentView('edit-shift');
				break;
			case 'event':
				_openEditEventModal();
				break;
			default:
				return;
		}
	};

	const openAddModal = (event: any) => {
		// console.log('Cell click event:', event);

		// Check if user has permission to create shifts
		if (calendarType === 'schedule' && !canManageShifts) {
			showToast('error', 'You do not have permission to create shifts');
			return;
		}

		// Determine the date to check
		const dateToCheck =
			event.action === 'drag' || event.action === 'externalDrop' ? event.event.start : event.date;

		// Check if the date is in the past
		if (calendarType === 'schedule' && isDateInPast(dateToCheck)) {
			showToast('error', 'Cannot add shifts on past dates');
			return;
		}

		setAddNewEvent(undefined);

		if (event.action === 'drag' || event.action === 'externalDrop') {
			setAddNewEvent({
				start_at: event.event.start,
				resource: event.event.resource,
				end_at: event.event.end,
				...event.event,
			});
			// Set the date to scroll to when returning
			setLastFocusedDate(event.event.start);
		} else {
			// Determine if we should prefill employee or job code based on calendar view
			let employeeFromResource = null;
			const jobCodeFromResource = event?.jobCodeFromResource;

			// In week/workweek view, resource is employee ID
			if (calendarView === 'week' || calendarView === 'workweek') {
				const resourceId = event.resource;
				// Skip if it's the "open-shifts" resource (house shifts)
				if (resourceId && resourceId !== 'open-shifts') {
					// Find the employee by ID from workplaceUsers
					const employee = workplaceUsers?.find(
						(user) => user.id === resourceId || user['@id'] === `/api/users/${resourceId}`
					);
					if (employee) {
						employeeFromResource = employee;
					}
				}
			}

			setAddNewEvent({
				start_at: event.date,
				resource: event.resource,
				employeeFromResource: employeeFromResource,
				jobCodeFromResource: jobCodeFromResource,
			});
			// Set the date to scroll to when returning
			setLastFocusedDate(event.date);
		}

		// Open the appropriate view/modal based on calendar type
		switch (calendarType) {
			case 'schedule':
				setCurrentView('add-shift');
				break;
			case 'event':
				_openAddEventModal();
				break;
			default:
				_openAddEventModal();
				break;
		}
	};

	// Employee request modal handlers
	const handleOpenEmployeeRequest = () => {
		setEditRequest(null);
		setCurrentView('employee-request');
	};

	// Request detail view handlers
	const handleOpenRequestDetail = (request: ShiftRequest) => {
		setSelectedRequestForDetail(request);
		setCurrentView('request-detail');
	};

	const handleCloseRequestDetail = () => {
		setCurrentView('scheduler');
		setSelectedRequestForDetail(null);
	};

	const handleApproveRequestFromDetail = async () => {
		if (selectedRequestForDetail) {
			await handleApproveRequest(selectedRequestForDetail.id);
			handleCloseRequestDetail();
		}
	};

	const handleDenyRequestFromDetail = async () => {
		if (selectedRequestForDetail) {
			await handleDenyRequest(selectedRequestForDetail.id);
			handleCloseRequestDetail();
		}
	};

	const handleApproveRequest = async (requestId: number) => {
		try {
			const request = selectedRequestForDetail;
			const requestType = request?.type?.label || '';

			await approveOrDenyShiftRequest(requestId, requestType, true);
			showToast('success', 'Request approved successfully!');
			queryClient.invalidateQueries(['shift-requests']);
			refetchShiftRequests();
			refetchShifts();

			// If it's a time-off request, refetch users to update their time-off data
			if (requestType && requestType.toLowerCase().includes('time')) {
				queryClient.invalidateQueries(['users-list', workplaceId]);
			}
		} catch (error: any) {
			showToast('error', error?.message || 'Failed to approve request');
		}
	};

	const handleDenyRequest = async (requestId: number) => {
		try {
			const request = selectedRequestForDetail;
			const requestType = request?.type?.label || '';

			await approveOrDenyShiftRequest(requestId, requestType, false);
			showToast('success', 'Request denied successfully!');
			queryClient.invalidateQueries(['shift-requests']);
			refetchShiftRequests();
			refetchShifts();
		} catch (error: any) {
			showToast('error', error?.message || 'Failed to deny request');
		}
	};

	const confirmDeleteRequest = () => {
		if (requestToDelete) {
			deleteShiftRequestMutation(requestToDelete);
		}
	};

	// Create a map of shifts by ID for quick lookup
	const shiftsMap = useMemo(() => {
		const map = new Map();
		allShifts.forEach((shift) => {
			map.set(shift.id, shift);
			map.set(shift['@id'], shift);
			// Also map API IRI format
			const iriId = `/api/shifts/${shift.id}`;
			map.set(iriId, shift);
		});
		return map;
	}, [allShifts]);

	const getEmployeeName = (
		employee:
			| ShiftRequest['employeeRequest']
			| ShiftRequest['employeeAssigned']
			| ShiftRequest['createdBy']
	) => {
		if (!employee) return 'N/A';
		return (
			`${employee?.firstName || ''} ${employee?.lastName || ''}`.trim() || employee?.email || 'N/A'
		);
	};

	const getShiftDates = (request: ShiftRequest) => {
		// For availability requests using day with startAt/endAt
		if (request.day && request.startAt && request.endAt) {
			// Find the day name from daysData
			const day = daysData?.find((d: any) => d['@id'] === request.day);
			const dayName = day?.label || 'Unknown Day';
			// Extract just the time portion (HH:mm) without timezone conversion or seconds
			const startTime = (request.startAt.split('T')[1] || request.startAt).substring(0, 5);
			const endTime = (request.endAt.split('T')[1] || request.endAt).substring(0, 5);
			return `${dayName}: ${startTime} - ${endTime}`;
		}

		// For availability requests using old format (dayStartTime/dayEndTime)
		if (request.day && request.dayStartTime && request.dayEndTime) {
			// Find the day name from daysData
			const day = daysData?.find((d: any) => d['@id'] === request.day);
			const dayName = day?.label || 'Unknown Day';
			// Extract only HH:mm without seconds
			const startTime = request.dayStartTime.substring(0, 5);
			const endTime = request.dayEndTime.substring(0, 5);
			return `${dayName}: ${startTime} - ${endTime}`;
		}

		// Try to get shift data from shiftRequest
		if (request.shiftRequest) {
			const shiftId =
				typeof request.shiftRequest === 'string'
					? request.shiftRequest
					: request.shiftRequest['@id'];
			const shift = shiftsMap.get(shiftId);
			if (shift) {
				return `${moment(shift.start).format('MM/DD/YYYY HH:mm')} - ${moment(shift.end).format('HH:mm')}`;
			}
		}

		// Try to get shift data from shiftAssigned
		if (request.shiftAssigned) {
			const shiftId =
				typeof request.shiftAssigned === 'string'
					? request.shiftAssigned
					: request.shiftAssigned['@id'];
			const shift = shiftsMap.get(shiftId);
			if (shift) {
				return `${moment(shift.start).format('MM/DD/YYYY HH:mm')} - ${moment(shift.end).format('HH:mm')}`;
			}
		}

		// Fallback to startAt/endAt if provided (for time-off requests)
		if (request.startAt && request.endAt) {
			// Check if this is a time-off request by checking the type label
			const requestType = request?.type?.label?.toLowerCase() || '';
			if (requestType.includes('time off') || requestType.includes('time-off')) {
				// For time-off requests, only show the dates without times

				return `${moment(request.startAt).format('MM/DD/YYYY')} - ${moment(request.endAt).format('MM/DD/YYYY')}`;
			}
			return `${moment(request.startAt).format('MM/DD/YYYY HH:mm')} - ${moment(request.endAt).format('HH:mm')}`;
		}

		return 'Not specified';
	};

	const handleEditStats = () => {
		setCurrentView('edit-stats');
	};

	const handleCopySchedule = () => {
		setCurrentView('copy-schedule');
	};

	const handleBackToSchedule = () => setCurrentView('scheduler');

	// Watch for business week changes from HeaderBar via context
	useEffect(() => {
		if (!businessWeekStart) return;

		const weekStartDate = moment(businessWeekStart);
		const weekEndDate = weekStartDate.clone().add(6, 'days');

		// Update the date range based on calendar view
		// let startDate, endDate;

		// Both day view and week view now show the entire business week
		// Day view has hourly granularity, week view has larger time blocks
		const startDate = weekStartDate.toDate();
		const endDate = weekEndDate.toDate();

		const [formatedFirstDay, formatedLastDay] = getTimeWithTimezone(startDate, endDate, timezones);

		setFromDate(formatedFirstDay);
		setToDate(formatedLastDay);

		// Reset last focused date when business week changes via header controls
		// This ensures the calendar scrolls to the start of the new week
		setLastFocusedDate(null);
	}, [businessWeekStart, timezones]);

	// Update the last focused date when navigating to a different week
	// This preserves the day position when switching between day/week views
	useEffect(() => {
		if (!calendarSelectedDate) return;

		// Only update if the date has actually changed (avoid unnecessary re-renders)
		if (!lastFocusedDate || calendarSelectedDate.getTime() !== lastFocusedDate.getTime()) {
			setLastFocusedDate(calendarSelectedDate);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [calendarSelectedDate]);
	// console.log('calendarSelectedDate', calendarSelectedDate, lastFocusedDate);
	const updateTimeRange = (firstday, lastday) => {
		const [formatedFirstDay, formatedLastDay] = getTimeWithTimezone(firstday, lastday, timezones);

		setFromDate(formatedFirstDay);
		setToDate(formatedLastDay);

		// Check if the user is looking at the next week to enable the publish schedule button
		const firstDayOfSelectedWeek = moment().startOf('week');
		const nextWeekStart = moment(firstDayOfSelectedWeek).add(7, 'days');
		const nextWeekEnd = moment(firstDayOfSelectedWeek).add(14, 'days');

		const isPublishable = moment(firstday).isBetween(nextWeekStart, nextWeekEnd, null, '[]');
		setIsPublishable(isPublishable);
	};

	const _closeEditModalHandler = () => {
		// Only handle events now - shifts use full-screen approach
		if (calendarType === 'event') {
			refetchEvents();
			_closeEditEventModal();
		}
	};

	const handleBackToScheduler = () => {
		setCurrentView('scheduler');
		setAddNewEvent(undefined);
		setEditEvent(null);
		refetchShifts();
	};

	const handleEditShift = (event: any) => {
		// Check if user has permission to edit shifts
		if (!canManageShifts) {
			showToast('error', 'You do not have permission to edit shifts');
			return;
		}

		// Check if the shift date is in the past
		if (isDateInPast(event.start || event.startAt)) {
			showToast('error', 'Cannot edit shifts on past dates');
			return;
		}

		openEditModal(event);
	};

	const handleDeleteShift = (event: any) => {
		// Check if user has permission to delete shifts
		if (!canDeleteShifts) {
			showToast('error', 'You do not have permission to delete shifts');
			return;
		}

		// Check if the shift date is in the past
		if (isDateInPast(event.start || event.startAt)) {
			showToast('error', 'Cannot delete shifts on past dates');
			return;
		}

		// Store the shift to delete and show confirmation dialog
		setShiftToDelete(event);
		setIsDeleteDialogOpen(true);
	};

	const handleCopyShift = (event: any) => {
		if (!canManageShifts) {
			showToast('error', 'You do not have permission to copy shifts');
			return;
		}

		// Check if the shift date is in the past
		if (isDateInPast(event.start || event.startAt)) {
			showToast('error', 'Cannot copy shifts on past dates');
			return;
		}

		// Build the payload for the new shift - same as original but employee is null (house shift)
		// event.start and event.end are Date objects - send as-is without timezone conversion
		const payload: IShiftPayload = {
			workplace: `api/workplaces/${workplaceId}`,
			startAt: moment(event.start).format('YYYY-MM-DDTHH:mm:ss'),
			endAt: moment(event.end).format('YYYY-MM-DDTHH:mm:ss'),
			employee: null, // Always create as house shift
			jobCode:
				event.jobCode?.['@id'] || (event.jobCode?.id ? `api/job_codes/${event.jobCode.id}` : null),
			startOfWeek: fromDate ? moment(fromDate).format('YYYY-MM-DD') : undefined,
		};
		copyShift(payload);
	};

	const confirmDeleteShift = () => {
		if (shiftToDelete) {
			// Use shift_id from calendar event or id from shift object
			const shiftId = shiftToDelete.shift_id || shiftToDelete.id;
			if (shiftId) {
				deleteShift(shiftId);
			}
		}
	};

	const _closeAddModalHandler = () => {
		// Only handle events now - shifts use full-screen approach
		if (calendarType === 'event') {
			refetchEvents();
			_closeAddEventModal();
		}
	};

	const openRequestsModal = (event: any): any => {
		setRequestData(event);

		switch (event.shift_type.id) {
			case 2:
				_openTimeOffRequestModal();
				break;
			case 3:
				_openShiftPickModal();
				break;
			case 4:
				_openRequestAvailableModalOpen();
				break;
			case 5:
				_openCoverRequestModal();
				break;
			case 6:
				_openTradeRequestModal();
				break;
			default:
				return null;
		}
	};

	const getBusinessWeekRange = useCallback(() => {
		const dayMap: { [key: string]: number } = {
			Sunday: 0,
			Monday: 1,
			Tuesday: 2,
			Wednesday: 3,
			Thursday: 4,
			Friday: 5,
			Saturday: 6,
		};

		const startDayOfWeek = dayMap[startOfWeek] ?? 1;
		const currentDate = moment(fromDate);
		const currentDayOfWeek = currentDate.day();

		let daysBack = currentDayOfWeek - startDayOfWeek;
		if (daysBack < 0) {
			daysBack += 7;
		}

		const businessWeekStart = currentDate.clone().subtract(daysBack, 'days').startOf('day');
		const businessWeekEnd = businessWeekStart.clone().add(6, 'days').endOf('day');

		return { businessWeekStart, businessWeekEnd };
	}, [fromDate, startOfWeek]);

	const filteredEventsForWeek = useMemo(() => {
		const { businessWeekStart, businessWeekEnd } = getBusinessWeekRange();
		return allEvents.filter((event: any) => {
			const eventStart = moment(event.startDate || event.start);
			return eventStart.isBetween(businessWeekStart, businessWeekEnd, null, '[]');
		});
	}, [allEvents, getBusinessWeekRange]);

	const handleEventClick = (event: any) => {
		history.push({
			pathname: '/manager/events/details',
			state: { event, eventId: event.id },
		});
	};

	const handleCloseEventDialog = () => {
		setIsEventDialogOpen(false);
		setSelectedEvent(null);
	};

	const filterEvents = () => {
		const newArrOfEvents: MbscCalendarEvent[] = [];
		_schedulesBackup.forEach((event) => {
			return filters.forEach((filter) => {
				if (filter.id === event?.shift_type?.id) {
					if (filter.active) {
						newArrOfEvents.push(event);
					}
				}
			});
		});

		setAllSchedules(newArrOfEvents);
	};

	const _filterReset = (type: boolean) => {
		const newArr = filters.map((value) => {
			value.active = type;
			return {
				...value,
			};
		});

		setFilters(newArr);
		filterEvents();
	};

	// Handle different view states
	if (currentView === 'add-shift') {
		return (
			<AddShiftPage
				onBack={handleBackToScheduler}
				addNewShift={addNewEvent}
				generalSettings={generalSettings}
				workplaceUsers={workplaceUsers}
				daysData={daysData}
				weekStartDate={fromDate ? new Date(fromDate) : undefined}
				workplaceWorkingHoursStart={workplaceWorkingHoursStart}
			/>
		);
	}

	if (currentView === 'edit-shift') {
		return (
			<AddShiftPage
				onBack={handleBackToScheduler}
				editShift={editEvent}
				generalSettings={generalSettings}
				workplaceUsers={workplaceUsers}
				daysData={daysData}
				weekStartDate={fromDate ? new Date(fromDate) : undefined}
			/>
		);
	}

	// Handle employee request view
	if (currentView === 'employee-request') {
		return (
			<EmployeeRequestModal
				closeModal={handleBackToSchedule}
				refetchSchedules={() => {
					refetchShifts();
					refetchShiftRequests();
				}}
				currentWeekStart={fromDate ? moment(fromDate).toDate() : undefined}
				currentWeekEnd={toDate ? moment(toDate).toDate() : undefined}
				editRequest={editRequest}
			/>
		);
	}

	// Handle request detail view
	if (currentView === 'request-detail' && selectedRequestForDetail) {
		return (
			<RequestDetailView
				request={selectedRequestForDetail}
				onApprove={handleApproveRequestFromDetail}
				onDeny={handleDenyRequestFromDetail}
				onClose={handleCloseRequestDetail}
				canManageRequests={canManageAllRequests}
				allShifts={allShifts}
			/>
		);
	}

	// Handle edit stats view
	if (currentView === 'edit-stats') {
		return <EditStatsPage onBack={handleBackToSchedule} startDate={fromDate} endDate={toDate} />;
	}

	// Handle copy schedule view
	if (currentView === 'copy-schedule') {
		return (
			<CopySchedulePage
				onBack={handleBackToSchedule}
				currentWeekStart={fromDate}
				currentWeekEnd={toDate}
				startOfWeek={startOfWeek}
			/>
		);
	}

	return (
		<>
			{/* Top requests / events box moved above the main Content to occupy full width */}
			<Grid
				container
				spacing={3}
				className="TopRequestsBox"
				direction="row"
				justifyContent={'space-between'}
			>
				<Grid className="TopRequestsSection" item xs={12} md={6}>
					<div className="TopSectionContainer">
						<div className="TopRequestsHeader">
							<h6>Employee Requests</h6>
							{canSubmitRequests && (
								<button
									onClick={handleOpenEmployeeRequest}
									onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgb(57, 80, 111)')}
									onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgb(68, 99, 136)')}
									style={{
										margin: '0 12px 0 auto',
										backgroundColor: 'rgb(68, 99, 136)',
										color: '#fff',
										height: '32px',
										borderRadius: '6px',
										fontSize: '14px',
										fontWeight: '500',
										fontFamily: 'Gotham',
										padding: '0 16px',
										border: 'none',
										cursor: 'pointer',
										textTransform: 'none',
									}}
								>
									Add
								</button>
							)}
						</div>
						<div className="TopRequestsBody">
							{/* <div
								style={{ padding: '12px 0', fontSize: '14px', color: '#666', fontWeight: '500' }}
							>
								Week: {fromDate ? moment(fromDate).format('MMM D') : ''} -{' '}
								{toDate ? moment(toDate).format('MMM D, YYYY') : ''}
							</div> */}
							<div className="TopRequestsTable">
								<div className="TopRequestsRow TopRequestsHead">
									<div className="col requestDate" style={{ flex: '0 0 110px' }}>
										Request Date
									</div>
									<div className="col employee" style={{ flex: '1 1 120px', marginLeft: '12px' }}>
										Employee
									</div>
									<div className="col type" style={{ flex: '1 1 120px', marginLeft: '12px' }}>
										Request Type
									</div>
									<div
										className="col scheduleDates"
										style={{ flex: '1 1 180px', marginLeft: '12px' }}
									>
										Schedule Dates
									</div>
								</div>
								<div className="TopRequestsRows">
									{isLoadingRequests ? (
										<div className="noRows" style={{ textAlign: 'center', padding: '24px' }}>
											<CircularProgress size={20} color="inherit" />
										</div>
									) : shiftRequestsData?.['member']?.length ? (
										shiftRequestsData['member']
											.filter((request: ShiftRequest) => {
												// Only show pending requests (employeeRequestConfirmed is null/undefined)
												// Exclude trade requests
												const isTradeRequest = request?.type?.label
													?.toLowerCase()
													?.includes('trade');

												// If user doesn't have manage all requests permission, only show their own requests
												const isOwnRequest =
													canManageAllRequests || request?.createdBy?.id === _auth?.user?.id;

												// For pickup requests, exclude house shifts that are before current date & time
												const isPickupRequest = request?.type?.label
													?.toLowerCase()
													?.includes('pickup');
												let shouldExcludeExpiredHouseShift = false;

												if (isPickupRequest && request.shiftRequest) {
													// Get the shift from shiftRequest
													const shiftId =
														typeof request.shiftRequest === 'string'
															? request.shiftRequest
															: request.shiftRequest['@id'];
													const shift = shiftsMap.get(shiftId);

													// Check if it's a house shift (no employee) and if it's in the past
													if (shift && !shift.employee) {
														const shiftEndTime = moment(shift.end);
														const now = moment();
														shouldExcludeExpiredHouseShift = shiftEndTime.isBefore(now);
													}
												}

												return (
													(request.employeeRequestConfirmed === null ||
														request.employeeRequestConfirmed === undefined) &&
													!isTradeRequest &&
													isOwnRequest &&
													!shouldExcludeExpiredHouseShift
												);
											})
											.sort((a: ShiftRequest, b: ShiftRequest) => {
												// Sort by created_at descending (latest first)
												const dateA = a?.created_at ? new Date(a.created_at).getTime() : 0;
												const dateB = b?.created_at ? new Date(b.created_at).getTime() : 0;
												return dateB - dateA;
											})
											.map((request: ShiftRequest) => (
												<div
													key={request?.id}
													className="TopRequestsRow"
													onClick={() => handleOpenRequestDetail(request)}
													style={{ cursor: 'pointer' }}
													onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
													onMouseLeave={(e) =>
														(e.currentTarget.style.backgroundColor = 'transparent')
													}
												>
													<div className="col requestDate" style={{ flex: '0 0 110px' }}>
														{request?.created_at
															? moment(request.created_at).tz(timezones).format('MM/DD/YYYY')
															: 'N/A'}
													</div>
													<div
														className="col employee"
														style={{ flex: '1 1 120px', marginLeft: '12px' }}
													>
														{getEmployeeName(request?.createdBy)}
													</div>
													<div
														className="col type"
														style={{ flex: '1 1 120px', marginLeft: '12px' }}
													>
														{request?.type?.label || 'Unknown'}
													</div>
													<div
														className="col scheduleDates"
														style={{ flex: '1 1 180px', marginLeft: '12px' }}
													>
														{getShiftDates(request)}
													</div>
												</div>
											))
									) : (
										<div className="noRows">No employee requests</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</Grid>
				<Grid className="TopRequestsSection" item xs={12} md={6}>
					<div className="TopSectionContainer">
						<div className="TopRequestsHeader">
							<h6>Events this Week</h6>
						</div>
						<div className="TopRequestsBody">
							<div className="TopRequestsTable">
								<div className="TopRequestsRow TopRequestsHead">
									<div className="col requestDate">Date</div>
									<div className="col employee">Title</div>
									<div className="col type">Type</div>
									<div className="col scheduleDates">When</div>
								</div>
								<div className="TopRequestsRows">
									{isLoadingEvents ? (
										<div className="noRows" style={{ textAlign: 'center', padding: '24px' }}>
											<CircularProgress size={20} color="inherit" />
										</div>
									) : filteredEventsForWeek.length ? (
										filteredEventsForWeek.map((e: any, i: number) => {
											// Get event type from pregenerated map using @id as key
											const eventType = _eventTypesMap[e.type];
											return (
												<div
													className="TopRequestsRow"
													key={e.id || i}
													onClick={() => handleEventClick(e)}
													style={{ cursor: 'pointer' }}
												>
													<div className="col requestDate">
														{moment(e.startDate || e.start).format('MM/DD/YYYY')}
													</div>
													<div className="col employee">{e.name || e.title}</div>
													<div className="col type">{eventType?.label || 'Event'}</div>
													<div className="col scheduleDates">
														{`${moment(e.startDate || e.start).format('h:mm A')} - ${moment(
															e.endDate || e.end
														).format('h:mm A')}`}
													</div>
												</div>
											);
										})
									) : (
										<div className="noRows">No events</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</Grid>
			</Grid>
			{/* Schedule Statistics Box */}
			<ScheduleStatsBox
				fromDate={fromDate}
				toDate={toDate}
				startOfWeek={startOfWeek}
				sales={statsWithMetrics.sales}
				splh={statsWithMetrics.splh}
				cplh={statsWithMetrics.cplh}
				hoursScheduled={statsWithMetrics.hoursScheduled}
				dailyOT={statsWithMetrics.dailyOT}
				weeklyOT={statsWithMetrics.weeklyOT}
				customers={statsWithMetrics.customers}
				onPublish={handleOpenPublishModal}
				onCopy={handleCopySchedule}
				onEdit={handleEditStats}
				canManageShifts={canManageShifts}
				canPublishShifts={canPublishShifts}
				canEditStats={canEditShifts}
				hasUnpublishedShifts={hasUnpublishedShifts}
			/>{' '}
			<Grid container spacing={2} className="schedule-grid">
				<Grid item xs={12}>
					<div className="draggable" style={{ position: 'relative' }}>
						{isLoadingShifts && (
							<Box
								sx={{
									position: 'absolute',
									top: 0,
									left: 0,
									right: 0,
									bottom: 0,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									backgroundColor: 'rgba(255, 255, 255, 0.8)',
									zIndex: 1000,
									borderRadius: '4px',
								}}
							>
								<CircularProgress size={40} color="inherit" />
							</Box>
						)}
						{isMobile ? (
							<MobilePage
								openEditShiftModal={openEditModal}
								allSchedules={allSchedules}
								allEvents={allEvents}
								updateTimeRange={updateTimeRange}
								setCalendarType={setCalendarTypeHandler}
								calendarType={calendarType}
								openRequestsModal={openRequestsModal}
							/>
						) : (
							<DesktopPage
								openEditShiftModal={openEditModal}
								openAddShiftModal={openAddModal}
								onShiftDragUpdate={handleShiftDragUpdate}
								allEvents={allShifts.map((shift) => ({
									...shift,
									// Dynamically assign resource and title based on current calendar view
									resource:
										calendarView === 'week' || calendarView === 'workweek'
											? shift.employee?.id || 'open-shifts'
											: shift.jobCode?.id,
									title:
										calendarView === 'week' || calendarView === 'workweek'
											? shift.jobCode?.label || 'Shift' // Show job code in week view
											: shift.employee
												? `${shift.employee.firstName} ${shift.employee.lastName}`
												: 'House Shift', // Show employee in day view
								}))}
								workplacesData={workplacesData!}
								updateTimeRange={updateTimeRange}
								setCalendarType={setCalendarTypeHandler}
								calendarType={calendarType}
								openRequestsModal={openRequestsModal}
								openAddEventModal={openAddModal}
								openEditEventModal={openEditModal}
								// reassignShiftToAnotherUser={reassignShiftToAnotherUser}
								refetch={refetchShifts}
								// updateOneShiftHandler={updateOneShiftHandler}
								dragTimeStep={15}
								resourcesOverride={resourcesToPass}
								// dataForCalendar={dataForCalendar}
								startOfDay={startOfDay}
								startOfWeek={startOfWeek}
								workplaceWorkingHoursStart={workplaceWorkingHoursStart}
								selectedDate={calendarSelectedDate}
								onEditShift={handleEditShift}
								onDeleteShift={handleDeleteShift}
								onCopyShift={handleCopyShift}
								onPublish={handleOpenPublishModal}
								canManageShifts={canManageShifts}
								canDeleteShifts={canDeleteShifts}
							/>
						)}
					</div>
				</Grid>
			</Grid>
			{/* Delete Confirmation Dialog */}
			{isDeleteDialogOpen && (
				<ConfirmDialog
					type="shift"
					isLoading={isDeleteLoading}
					handleDeleteClick={confirmDeleteShift}
					setConfirmDeleteIsOpen={setIsDeleteDialogOpen}
				/>
			)}
			{/* Delete Employee Request Confirmation Dialog */}
			<Dialog open={deleteRequestConfirmOpen} onClose={() => setDeleteRequestConfirmOpen(false)}>
				<DialogTitle>Delete Request</DialogTitle>
				<DialogContent>
					<MuiTypography>Are you sure you want to delete this employee request?</MuiTypography>
				</DialogContent>
				<DialogActions>
					<MuiButton onClick={() => setDeleteRequestConfirmOpen(false)}>Cancel</MuiButton>
					<MuiButton onClick={confirmDeleteRequest} color="error" disabled={isDeletingRequest}>
						{isDeletingRequest ? 'Deleting...' : 'Delete'}
					</MuiButton>
				</DialogActions>
			</Dialog>
			{openPublishModal && (
				<PublishShiftsModal
					workplaceId={workplaceId}
					initialStartDate={fromDate}
					initialEndDate={toDate}
					onPublish={handlePublishShifts}
					onClose={() => setOpenPublishModal(false)}
				/>
			)}
			{/* Event Detail Dialog */}
			<Dialog open={isEventDialogOpen} onClose={handleCloseEventDialog} maxWidth="sm" fullWidth>
				<DialogTitle sx={{ fontFamily: 'Gotham', fontWeight: 600, color: 'rgb(68, 99, 136)' }}>
					Event Details
				</DialogTitle>
				<DialogContent>
					{selectedEvent && (
						<Box sx={{ pt: 1 }}>
							<Box sx={{ mb: 2 }}>
								<MuiTypography
									sx={{
										fontFamily: 'Gotham',
										fontSize: '12px',
										fontWeight: 600,
										color: '#666',
										mb: 0.5,
									}}
								>
									Title
								</MuiTypography>
								<MuiTypography sx={{ fontFamily: 'Gotham', fontSize: '14px' }}>
									{selectedEvent.name || selectedEvent.title}
								</MuiTypography>
							</Box>
							<Box sx={{ mb: 2 }}>
								<MuiTypography
									sx={{
										fontFamily: 'Gotham',
										fontSize: '12px',
										fontWeight: 600,
										color: '#666',
										mb: 0.5,
									}}
								>
									Type
								</MuiTypography>
								<MuiTypography sx={{ fontFamily: 'Gotham', fontSize: '14px' }}>
									{_eventTypesMap[selectedEvent.type]?.label || 'Event'}
								</MuiTypography>
							</Box>
							<Box sx={{ mb: 2 }}>
								<MuiTypography
									sx={{
										fontFamily: 'Gotham',
										fontSize: '12px',
										fontWeight: 600,
										color: '#666',
										mb: 0.5,
									}}
								>
									Date
								</MuiTypography>
								<MuiTypography sx={{ fontFamily: 'Gotham', fontSize: '14px' }}>
									{moment(selectedEvent.startDate || selectedEvent.start)
										.tz(timezones)
										.format('MMMM DD, YYYY')}
								</MuiTypography>
							</Box>
							<Box sx={{ mb: 2 }}>
								<MuiTypography
									sx={{
										fontFamily: 'Gotham',
										fontSize: '12px',
										fontWeight: 600,
										color: '#666',
										mb: 0.5,
									}}
								>
									Time
								</MuiTypography>
								<MuiTypography sx={{ fontFamily: 'Gotham', fontSize: '14px' }}>
									{moment(selectedEvent.startDate || selectedEvent.start)
										.tz(timezones)
										.format('h:mm A')}{' '}
									-{' '}
									{moment(selectedEvent.endDate || selectedEvent.end)
										.tz(timezones)
										.format('h:mm A')}
								</MuiTypography>
							</Box>
							{selectedEvent.description && (
								<Box sx={{ mb: 2 }}>
									<MuiTypography
										sx={{
											fontFamily: 'Gotham',
											fontSize: '12px',
											fontWeight: 600,
											color: '#666',
											mb: 0.5,
										}}
									>
										Description
									</MuiTypography>
									<MuiTypography sx={{ fontFamily: 'Gotham', fontSize: '14px' }}>
										{selectedEvent.description}
									</MuiTypography>
								</Box>
							)}
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<MuiButton
						onClick={handleCloseEventDialog}
						sx={{
							fontFamily: 'Gotham',
							textTransform: 'none',
							color: 'rgb(68, 99, 136)',
							fontWeight: 500,
						}}
					>
						Close
					</MuiButton>
				</DialogActions>
			</Dialog>
			{/* All employee requests now render as full-screen views using conditional rendering above */}
		</>
	);
}
